import { useRef, useState, useCallback, useEffect } from 'react'
import { useRoomContext, useLocalParticipant } from '@livekit/components-react'
import { RoomEvent } from 'livekit-client'

// ── Types ──

export type AnnotTool = 'pen' | 'highlighter' | 'arrow' | 'rectangle' | 'text' | 'eraser'

export interface AnnotPoint {
  x: number // 0-1 normalised
  y: number // 0-1 normalised
}

export interface AnnotStroke {
  id: string
  tool: AnnotTool
  points: AnnotPoint[]
  color: string
  width: number
  text?: string // for 'text' tool
  author: string
}

// DataChannel message format
export interface AnnotationMessage {
  type: 'annotation'
  action: 'stroke' | 'clear' | 'undo' | 'erase' | 'toggle'
  data?: AnnotStroke
  enabled?: boolean // for toggle
}

const encoder = new TextEncoder()
const decoder = new TextDecoder()

let _idCounter = 0
function annotId(identity: string): string {
  return `${identity}-${Date.now()}-${++_idCounter}`
}

// ── Hook ──

export function useAnnotation() {
  const room = useRoomContext()
  const { localParticipant } = useLocalParticipant()
  const [strokes, setStrokes] = useState<AnnotStroke[]>([])
  const [annotationsEnabled, setAnnotationsEnabled] = useState(true)
  const strokesRef = useRef<AnnotStroke[]>([])
  strokesRef.current = strokes

  // Send annotation data to all participants
  const broadcast = useCallback((msg: AnnotationMessage) => {
    try {
      const payload = encoder.encode(JSON.stringify(msg))
      room.localParticipant.publishData(payload, { reliable: true })
    } catch (err) {
      console.warn('[annotation] broadcast failed:', err)
    }
  }, [room])

  // Add a completed stroke (local + broadcast)
  const addStroke = useCallback((stroke: Omit<AnnotStroke, 'id' | 'author'>) => {
    const full: AnnotStroke = {
      ...stroke,
      id: annotId(localParticipant.identity),
      author: localParticipant.identity,
    }
    setStrokes(prev => [...prev, full])
    broadcast({ type: 'annotation', action: 'stroke', data: full })
  }, [localParticipant.identity, broadcast])

  // Undo last own stroke
  const undoLast = useCallback(() => {
    const own = strokesRef.current.filter(s => s.author === localParticipant.identity)
    if (own.length === 0) return
    const lastId = own[own.length - 1].id
    setStrokes(prev => prev.filter(s => s.id !== lastId))
    broadcast({ type: 'annotation', action: 'undo', data: { id: lastId } as any })
  }, [localParticipant.identity, broadcast])

  // Remove a specific stroke by ID (eraser)
  const removeStroke = useCallback((id: string) => {
    setStrokes(prev => prev.filter(s => s.id !== id))
    broadcast({ type: 'annotation', action: 'erase', data: { id } as any })
  }, [broadcast])

  // Clear all strokes (admin/sharer action)
  const clearAll = useCallback(() => {
    setStrokes([])
    broadcast({ type: 'annotation', action: 'clear' })
  }, [broadcast])

  // Toggle annotations for others (sharer control)
  const toggleAnnotations = useCallback((enabled: boolean) => {
    setAnnotationsEnabled(enabled)
    broadcast({ type: 'annotation', action: 'toggle', enabled })
  }, [broadcast])

  // Receive remote annotations
  useEffect(() => {
    if (!room) return

    const handler = (payload: Uint8Array) => {
      let msg: AnnotationMessage
      try {
        msg = JSON.parse(decoder.decode(payload))
      } catch { return }
      if (msg.type !== 'annotation') return

      if (msg.action === 'stroke' && msg.data) {
        setStrokes(prev => [...prev, msg.data!])
      } else if (msg.action === 'undo' && msg.data?.id) {
        setStrokes(prev => prev.filter(s => s.id !== msg.data!.id))
      } else if (msg.action === 'erase' && msg.data?.id) {
        setStrokes(prev => prev.filter(s => s.id !== msg.data!.id))
      } else if (msg.action === 'clear') {
        setStrokes([])
      } else if (msg.action === 'toggle' && msg.enabled !== undefined) {
        setAnnotationsEnabled(msg.enabled)
      }
    }

    room.on(RoomEvent.DataReceived, handler)
    return () => { room.off(RoomEvent.DataReceived, handler) }
  }, [room])

  return {
    strokes,
    addStroke,
    undoLast,
    removeStroke,
    clearAll,
    annotationsEnabled,
    toggleAnnotations,
    identity: localParticipant.identity,
  }
}
